import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Users, CalendarCheck, BarChart3, LogOut, Clock, Mail, Phone, MapPin, ChevronDown, ChevronUp, CheckCircle, XCircle, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getAuthHeaders() {
  const token = localStorage.getItem("admin_token");
  return { Authorization: `Bearer ${token}` };
}

const planLabels = {
  weekly: "Weekly Elite ($250)",
  biweekly: "Bi-Weekly Pro ($140)",
  monthly: "Monthly Essentials ($100)",
};

const statusColors = {
  pending: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20",
  confirmed: "bg-green-500/10 text-green-400 border-green-500/20",
  completed: "bg-blue-500/10 text-blue-400 border-blue-500/20",
  cancelled: "bg-red-500/10 text-red-400 border-red-500/20",
};

export default function AdminDashboard() {
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const [statsRes, leadsRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/leads`, { headers }),
        axios.get(`${API}/admin/bookings`, { headers }),
      ]);
      setStats(statsRes.data);
      setLeads(leadsRes.data);
      setBookings(bookingsRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/admin");
        toast.error("Session expired. Please log in again.");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) { navigate("/admin"); return; }
    fetchData();
  }, [navigate, fetchData]);

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_name");
    navigate("/admin");
  };

  const updateBookingStatus = async (bookingId, status) => {
    try {
      await axios.patch(`${API}/admin/bookings/${bookingId}/status`, { status }, { headers: getAuthHeaders() });
      toast.success(`Booking ${status}`);
      fetchData();
    } catch {
      toast.error("Failed to update status");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#C5A059] animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#0A0A0B] pt-24 pb-16">
      <div className="px-6 md:px-12 lg:px-24">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl tracking-tight font-medium text-[#F3F4F6]">
              Dashboard
            </h1>
            <p className="text-sm text-[#71717A]">Welcome back, {localStorage.getItem("admin_name") || "Admin"}</p>
          </div>
          <button
            data-testid="admin-logout-btn"
            onClick={handleLogout}
            className="flex items-center gap-2 text-sm text-[#A1A1AA] hover:text-[#C5A059] transition-colors"
          >
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-8 border-b border-white/5">
          {[
            { id: "overview", label: "Overview", icon: BarChart3 },
            { id: "leads", label: "Leads", icon: Users },
            { id: "bookings", label: "Bookings", icon: CalendarCheck },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
                tab === t.id
                  ? "border-[#C5A059] text-[#C5A059]"
                  : "border-transparent text-[#71717A] hover:text-[#A1A1AA]"
              }`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {tab === "overview" && stats && (
          <div data-testid="overview-tab">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {[
                { label: "Total Leads", value: stats.total_leads, icon: Users },
                { label: "Total Bookings", value: stats.total_bookings, icon: CalendarCheck },
                { label: "Leads (7 days)", value: stats.recent_leads_7d, icon: ChevronUp },
                { label: "Bookings (7 days)", value: stats.recent_bookings_7d, icon: ChevronUp },
              ].map((s, i) => (
                <div key={i} data-testid={`stat-card-${i}`} className="p-6 rounded-md bg-[#141416] border border-white/5">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs text-[#71717A] uppercase tracking-wider">{s.label}</span>
                    <s.icon className="w-4 h-4 text-[#C5A059]" />
                  </div>
                  <p className="font-heading text-3xl tracking-tight font-medium text-[#F3F4F6]">{s.value}</p>
                </div>
              ))}
            </div>

            {/* Plan Breakdown */}
            <div className="p-6 rounded-md bg-[#141416] border border-white/5 max-w-lg">
              <h3 className="font-heading text-lg font-medium text-[#F3F4F6] mb-4">Plan Breakdown</h3>
              <div className="space-y-3">
                {Object.entries(stats.plan_breakdown || {}).map(([plan, count]) => (
                  <div key={plan} className="flex items-center justify-between">
                    <span className="text-sm text-[#A1A1AA]">{planLabels[plan] || plan}</span>
                    <span className="text-sm text-[#F3F4F6] font-medium">{count}</span>
                  </div>
                ))}
                {Object.keys(stats.plan_breakdown || {}).length === 0 && (
                  <p className="text-sm text-[#71717A]">No data yet</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Leads Tab */}
        {tab === "leads" && (
          <div data-testid="leads-tab">
            <p className="text-sm text-[#71717A] mb-4">{leads.length} total leads</p>
            {leads.length === 0 ? (
              <p className="text-[#71717A] text-center py-12">No leads yet. They'll appear here when customers fill out the funnel.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-3 px-4 text-[#71717A] font-medium">Name</th>
                      <th className="text-left py-3 px-4 text-[#71717A] font-medium">Email</th>
                      <th className="text-left py-3 px-4 text-[#71717A] font-medium">Phone</th>
                      <th className="text-left py-3 px-4 text-[#71717A] font-medium">Zip</th>
                      <th className="text-left py-3 px-4 text-[#71717A] font-medium">Plan</th>
                      <th className="text-left py-3 px-4 text-[#71717A] font-medium">Source</th>
                      <th className="text-left py-3 px-4 text-[#71717A] font-medium">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map((lead, i) => (
                      <tr key={i} data-testid={`lead-row-${i}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                        <td className="py-3 px-4 text-[#F3F4F6] font-medium">{lead.name}</td>
                        <td className="py-3 px-4 text-[#A1A1AA]">
                          <a href={`mailto:${lead.email}`} className="hover:text-[#C5A059]">{lead.email}</a>
                        </td>
                        <td className="py-3 px-4 text-[#A1A1AA]">
                          <a href={`tel:${lead.phone}`} className="hover:text-[#C5A059]">{lead.phone}</a>
                        </td>
                        <td className="py-3 px-4 text-[#A1A1AA]">{lead.zip_code}</td>
                        <td className="py-3 px-4">
                          <span className="text-[#C5A059] text-xs font-medium">{planLabels[lead.recommended_plan] || lead.recommended_plan || "—"}</span>
                        </td>
                        <td className="py-3 px-4 text-[#71717A] text-xs">{lead.source}</td>
                        <td className="py-3 px-4 text-[#71717A] text-xs">{new Date(lead.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Bookings Tab */}
        {tab === "bookings" && (
          <div data-testid="bookings-tab">
            <p className="text-sm text-[#71717A] mb-4">{bookings.length} total bookings</p>
            {bookings.length === 0 ? (
              <p className="text-[#71717A] text-center py-12">No bookings yet.</p>
            ) : (
              <div className="space-y-4">
                {bookings.map((b, i) => (
                  <div key={i} data-testid={`booking-card-${i}`} className="p-6 rounded-md bg-[#141416] border border-white/5">
                    <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                      <div>
                        <h3 className="text-[#F3F4F6] font-medium text-base">{b.name}</h3>
                        <p className="text-[#C5A059] text-sm font-medium">{planLabels[b.plan] || b.plan}</p>
                      </div>
                      <span className={`px-3 py-1 rounded-sm text-xs font-medium border ${statusColors[b.status] || statusColors.pending}`}>
                        {b.status}
                      </span>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4 text-sm">
                      <div className="flex items-center gap-2 text-[#A1A1AA]">
                        <CalendarCheck className="w-4 h-4 text-[#71717A]" /> {b.preferred_date}
                      </div>
                      <div className="flex items-center gap-2 text-[#A1A1AA]">
                        <Clock className="w-4 h-4 text-[#71717A]" /> {b.preferred_time}
                      </div>
                      <div className="flex items-center gap-2 text-[#A1A1AA]">
                        <Phone className="w-4 h-4 text-[#71717A]" />
                        <a href={`tel:${b.phone}`} className="hover:text-[#C5A059]">{b.phone}</a>
                      </div>
                      <div className="flex items-center gap-2 text-[#A1A1AA]">
                        <Mail className="w-4 h-4 text-[#71717A]" />
                        <a href={`mailto:${b.email}`} className="hover:text-[#C5A059]">{b.email}</a>
                      </div>
                    </div>
                    {b.notes && (
                      <p className="text-xs text-[#71717A] mb-4">Notes: {b.notes}</p>
                    )}
                    <div className="flex gap-2">
                      {b.status === "pending" && (
                        <>
                          <button
                            data-testid={`confirm-booking-${i}`}
                            onClick={() => updateBookingStatus(b.id, "confirmed")}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-green-500/10 text-green-400 border border-green-500/20 text-xs font-medium hover:bg-green-500/20 transition-colors"
                          >
                            <CheckCircle className="w-3.5 h-3.5" /> Confirm
                          </button>
                          <button
                            data-testid={`cancel-booking-${i}`}
                            onClick={() => updateBookingStatus(b.id, "cancelled")}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-red-500/10 text-red-400 border border-red-500/20 text-xs font-medium hover:bg-red-500/20 transition-colors"
                          >
                            <XCircle className="w-3.5 h-3.5" /> Cancel
                          </button>
                        </>
                      )}
                      {b.status === "confirmed" && (
                        <button
                          data-testid={`complete-booking-${i}`}
                          onClick={() => updateBookingStatus(b.id, "completed")}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-blue-500/10 text-blue-400 border border-blue-500/20 text-xs font-medium hover:bg-blue-500/20 transition-colors"
                        >
                          <CheckCircle className="w-3.5 h-3.5" /> Mark Complete
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
